import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Apple, Droplet, CalendarCheck, Smile, Check, Wand2, ArrowRight, X, RotateCcw, Shuffle, ChevronRight } from 'lucide-react';
import { C } from '../lib/palette';
import { Card, Btn, Pill, SectionLabel } from '../components/base';
import { useMeta } from '../lib/useMeta';
import { useCompanion, COLLECTION_SIZE, SKY_SIZE } from '../lib/companionStore';
import GrowthTree from '../components/GrowthTree';
import CreatureCompanion from '../components/metaphors/CreatureCompanion';
import CollectionBoard from '../components/metaphors/CollectionBoard';
import ConstellationSky from '../components/metaphors/ConstellationSky';

const ACTIONS = [
  { id: 'nourish', icon: Apple, label: 'Nourish', toast: 'Nourished for today' },
  { id: 'hydrate', icon: Droplet, label: 'Hydrate', toast: 'Hydrated' },
  { id: 'weekly', icon: CalendarCheck, label: 'Weekly check-in', toast: 'Weekly check-in done' },
  { id: 'mood', icon: Smile, label: 'Mood', toast: 'Mood noted' },
];

const METAPHORS = [
  { id: 'growing', name: 'Growing', desc: 'A tree that fills out, leaf by leaf, as the days add up.' },
  { id: 'creature', name: 'Creature', desc: 'A little companion that hatches, grows up, and gains character.' },
  { id: 'collection', name: 'Collection', desc: 'A set of small keepsakes that unlock one at a time.' },
  { id: 'constellation', name: 'Constellation', desc: 'A night sky that gains a star for every day you show up.' },
];

const CAPS = {
  growing: ['Just getting started.', 'Finding its feet.', 'Branching out.', 'Really filling in.', 'Thriving.'],
  creature: ['Just hatched.', 'Settling in.', 'Growing bolder.', 'Full of character.', 'In full bloom.'],
};
const DORMANT_CAP = {
  growing: 'Resting — pick back up anytime.',
  creature: 'Taking a little nap — all is well.',
  collection: 'Your set is safe — resting for now.',
  constellation: 'A quiet night — no new stars.',
};

function caption({ metaphor, stage, activeCount, dormant }) {
  if (dormant) return DORMANT_CAP[metaphor];
  if (metaphor === 'collection') {
    const u = Math.min(COLLECTION_SIZE, activeCount);
    return u >= COLLECTION_SIZE ? 'Your set is complete.' : `${u} of ${COLLECTION_SIZE} collected`;
  }
  if (metaphor === 'constellation') {
    const s = Math.min(SKY_SIZE, activeCount);
    return `${s} ${s === 1 ? 'star' : 'stars'} in your sky`;
  }
  return CAPS[metaphor][stage - 1];
}

function MetaphorVisual({ api }) {
  const { metaphor, stage, activeCount, dormant } = api;
  if (metaphor === 'growing') return <GrowthTree stage={stage} dormant={dormant} className="mx-auto max-w-[240px]" />;
  if (metaphor === 'creature') return <CreatureCompanion stage={stage} dormant={dormant} className="mx-auto max-w-[240px]" />;
  if (metaphor === 'collection') return <CollectionBoard activeCount={activeCount} dormant={dormant} className="mx-auto max-w-[280px]" />;
  return <ConstellationSky activeCount={activeCount} stage={stage} dormant={dormant} className="mx-auto max-w-[260px]" />;
}

/* ---------- First-use picker ---------- */
function MetaphorPicker({ onPick }) {
  const preview = (id) => {
    if (id === 'growing') return <GrowthTree stage={3} />;
    if (id === 'creature') return <CreatureCompanion stage={3} />;
    if (id === 'collection') return <CollectionBoard activeCount={5} />;
    return <ConstellationSky activeCount={8} stage={3} />;
  };
  return (
    <div className="shell safe-top" style={{ background: C.bg }} data-testid="metaphor-picker">
      <div className="px-4 pb-16 pt-10">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="font-serif text-lg" style={{ color: C.teal }}>Companion</span>
          <h1 className="mt-3 font-serif text-3xl leading-tight" style={{ color: C.ink }}>Choose your companion</h1>
          <p className="mt-2 text-sm" style={{ color: C.inkSoft }}>How do you want to see your consistency build up over time? Pick the one that feels like yours — it becomes your whole experience.</p>
        </motion.div>

        <div className="mt-6 space-y-3">
          {METAPHORS.map((m, i) => (
            <motion.button
              key={m.id}
              data-testid={`pick-${m.id}`}
              onClick={() => onPick(m.id)}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.06 * i }}
              className="tap flex w-full items-center gap-3 rounded-2xl p-3 text-left"
              style={{ background: C.card, border: `1px solid ${C.lineSoft}`, boxShadow: '0 1px 8px rgba(44,90,78,0.06)' }}
            >
              <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl" style={{ background: m.id === 'constellation' ? 'transparent' : '#EDF2EE' }}>
                {preview(m.id)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg" style={{ color: C.ink }}>{m.name}</p>
                <p className="text-sm leading-snug" style={{ color: C.inkSoft }}>{m.desc}</p>
                <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: C.teal }}>Choose this <ChevronRight size={13} /></span>
              </div>
            </motion.button>
          ))}
        </div>
        <p className="mt-6 text-center text-xs" style={{ color: C.line }}>You can switch companion anytime — your care stays the same.</p>
      </div>
    </div>
  );
}

/* ---------- Guided walkthrough ---------- */
const TOUR = [
  { text: 'Meet your companion. A little care each day helps it grow — no numbers, no pressure.', run: (a) => a.resetProgress() },
  { text: "Tap through today's care and watch it respond.", run: (a) => a.logToday() },
  { text: 'Keep showing up — a few steady days and it starts to find its feet.', run: (a) => { a.advanceAndLog(); a.advanceAndLog(); } },
  { text: 'A week of small check-ins and it really begins to fill in.', run: (a) => { a.advanceAndLog(); a.advanceAndLog(); a.advanceAndLog(); } },
  { text: 'Life happens — skip a few days. Your companion simply rests. Nothing you have built goes away.', run: (a) => a.skipDays(3) },
  { text: 'Pick back up whenever you are ready — it carries on right where you left off.', run: (a) => { a.logToday(); for (let i = 0; i < 7; i++) a.advanceAndLog(); } },
  { text: "That's the whole idea: gentle daily care, shown your way.", run: () => {} },
];

function CompanionInner({ api, autoTour }) {
  const [tourActive, setTourActive] = useState(false);
  const [step, setStep] = useState(0);
  const started = useRef(false);
  const cap = caption(api);

  const startTour = () => { setTourActive(true); setStep(0); TOUR[0].run(api); };
  const nextTour = () => {
    const n = step + 1;
    if (n >= TOUR.length) { setTourActive(false); toast.success('Walkthrough complete'); return; }
    setStep(n); TOUR[n].run(api);
  };

  useEffect(() => {
    if (autoTour && !started.current) { started.current = true; startTour(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTour]);

  return (
    <div className="shell safe-top" style={{ background: C.bg }} data-testid="companion-product">
      <div className="px-4 pb-28 pt-5">
        <div className="flex items-center justify-between">
          <span className="font-serif text-lg" style={{ color: C.teal }}>Companion</span>
          <Pill tone="teal" data-testid="active-days">Day {api.day}</Pill>
        </div>

        {/* companion display */}
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="mt-3">
          <Card className="overflow-hidden p-2" style={{ background: api.metaphor === 'constellation' ? C.card : 'linear-gradient(180deg,#EDF2EE,#F3F5F1)' }}>
            <div className="min-h-[220px] py-1"><MetaphorVisual api={api} /></div>
            <div className="px-3 pb-2 text-center">
              <div className="mb-2 flex justify-center gap-1.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <span key={s} className="h-1.5 rounded-full transition-all" style={{ width: s <= api.stage ? 22 : 8, background: s <= api.stage ? (api.dormant ? C.line : C.teal) : C.lineSoft }} />
                ))}
              </div>
              <p className="font-serif text-base italic" style={{ color: api.dormant ? C.inkSoft : C.teal }} data-testid="companion-caption">{cap}</p>
              {api.dormant && <p className="mt-0.5 text-xs" style={{ color: C.line }}>Welcome back whenever you like.</p>}
            </div>
          </Card>
        </motion.div>

        {/* progress */}
        <div className="mt-3 flex items-center justify-between rounded-md px-3 py-2 text-xs" style={{ background: C.tealSoft, color: C.teal }}>
          <span><b>{api.activeCount}</b> {api.activeCount === 1 ? 'day' : 'days'} of care</span>
          {api.nextMilestone ? <span style={{ color: C.inkSoft }}>Next growth at {api.nextMilestone} days</span> : <span style={{ color: C.gold }}>Fully grown ✦</span>}
        </div>

        {/* care actions */}
        <div className="mt-4">
          <SectionLabel>Today's care</SectionLabel>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {ACTIONS.map((a) => {
              const Icon = a.icon;
              const done = !!api.todayActions[a.id];
              return (
                <button
                  key={a.id}
                  data-testid={`care-${a.id}`}
                  data-done={done ? '1' : '0'}
                  onClick={() => { if (done) return; api.log(a.id); toast.success(a.toast); }}
                  className="tap flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-medium transition-colors"
                  style={done ? { background: C.tealSoft, border: `1px solid ${C.teal}`, color: C.teal } : { background: '#fff', border: `1px solid ${C.lineSoft}`, color: C.ink }}
                >
                  {done ? <Check size={17} style={{ color: C.teal }} /> : <Icon size={17} style={{ color: C.teal }} />} {a.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* demo controls */}
        <Card className="mt-5 p-3" data-testid="demo-controls">
          <div className="flex items-center justify-between">
            <SectionLabel>Try it out</SectionLabel>
            <span className="text-[0.66rem]" style={{ color: C.line }}>Demo controls</span>
          </div>
          <p className="mt-1 text-xs" style={{ color: C.inkSoft }}>See the product work end to end without entering your own data.</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Btn variant="secondary" data-testid="demo-advance" onClick={() => { api.advanceDay(); }}><ArrowRight size={14} /> Advance a day</Btn>
            <Btn variant="secondary" data-testid="demo-reset" onClick={() => { api.resetProgress(); toast('Progress reset'); }}><RotateCcw size={14} /> Reset</Btn>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Btn variant="primary" data-testid="tour-start" onClick={startTour} disabled={tourActive}><Wand2 size={14} /> Guided walkthrough</Btn>
            <Btn variant="ghost" data-testid="switch-companion" onClick={() => { api.switchCompanion(); }}><Shuffle size={14} /> Switch companion</Btn>
          </div>
        </Card>
      </div>

      {/* walkthrough overlay */}
      <AnimatePresence>
        {tourActive && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            className="fixed bottom-0 inset-x-0 z-50 mx-auto w-full max-w-[30rem] px-3 pb-3 safe-bottom"
            data-testid="tour-panel"
          >
            <Card className="p-3" style={{ borderColor: C.gold, boxShadow: '0 -4px 24px rgba(44,90,78,0.18)' }}>
              <div className="flex items-center justify-between">
                <Pill tone="gold">Walkthrough · {step + 1}/{TOUR.length}</Pill>
                <button data-testid="tour-end" onClick={() => setTourActive(false)} style={{ color: C.gold }}><X size={16} /></button>
              </div>
              <p className="mt-2 text-sm" style={{ color: C.ink }}>{TOUR[step].text}</p>
              <Btn variant="gold" className="mt-2 w-full" data-testid="tour-next" onClick={nextTour}>{step + 1 >= TOUR.length ? 'Finish' : 'Next'} <ArrowRight size={15} /></Btn>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Companion() {
  useMeta({ title: 'Companion — grow a daily habit, your way', path: '' });
  const api = useCompanion();
  const loc = useLocation();
  const wantTour = new URLSearchParams(loc.search).get('tour') === '1';

  useEffect(() => {
    if (wantTour && !api.metaphor) api.pickMetaphor('growing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!api.metaphor) return <MetaphorPicker onPick={api.pickMetaphor} />;
  return <CompanionInner api={api} autoTour={wantTour} />;
}
